import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CHANGE_CLASSES, type CreateMissionInput } from "@bgreen/shared";
import { get, post } from "../lib/api";
import { useAction, useMe } from "../lib/hooks";
import type { ProjectOverview } from "../lib/types";
import { Button } from "./ui/button";
import { Dialog, Field, Input, Select, Textarea } from "./ui/primitives";

interface Ctx { isOpen: boolean; open: (o?: { projectId?: string; repositoryId?: string }) => void; close: () => void; preset: { projectId?: string; repositoryId?: string } }
const NewCtx = createContext<Ctx>({ isOpen: false, open: () => {}, close: () => {}, preset: {} });
export const useNewMission = () => useContext(NewCtx);

export function NewMissionProvider({ children }: { children: ReactNode }) {
  const [isOpen, setOpen] = useState(false);
  const [preset, setPreset] = useState<Ctx["preset"]>({});
  const [sp, setSp] = useSearchParams();
  const location = useLocation();
  const me = useMe();
  const canCreate = me.data?.user.role === "admin" || me.data?.user.role === "operator";

  useEffect(() => {
    if (sp.get("new") !== "1") return;
    if (location.pathname === "/missions" && canCreate) setOpen(true);
    const next = new URLSearchParams(sp);
    next.delete("new");
    setSp(next, { replace: true });
  }, [canCreate, location.pathname, sp, setSp]);

  useEffect(() => {
    if (!canCreate || location.pathname !== "/missions") setOpen(false);
  }, [canCreate, location.pathname]);

  const value = useMemo<Ctx>(() => ({
    isOpen,
    preset,
    open: (o) => {
      if (!canCreate || location.pathname !== "/missions") return;
      setPreset(o ?? {});
      setOpen(true);
    },
    close: () => setOpen(false),
  }), [canCreate, isOpen, location.pathname, preset]);
  return <NewCtx.Provider value={value}>{children}</NewCtx.Provider>;
}

const CLASS_LABEL: Record<string, string> = {
  other: "Normal (recomendado)", typo: "Corrección tipográfica", static_copy: "Cambio de texto estático", docs: "Documentación no sensible", trivial_ui: "Ajuste visual trivial", test_tweak: "Ajuste menor de tests",
};

export function NewMissionDialog() {
  const { isOpen, close, preset } = useNewMission();
  const nav = useNavigate();
  const projects = useQuery({ queryKey: ["projects"], queryFn: () => get<ProjectOverview[]>("/projects"), enabled: isOpen });
  const [f, setF] = useState({ title: "", objective: "", projectId: "", repositoryId: "", priority: "medium", changeClass: "other", budgetM: "" });
  const list = projects.data ?? [];
  useEffect(() => {
    if (!isOpen || !list.length) return;
    const p = list.find((x) => x.id === (preset.projectId || f.projectId)) ?? list[0]!;
    const repos = p.repositories.filter((r) => r.authorized);
    setF((s) => ({ ...s, projectId: p.id, repositoryId: repos.find((r) => r.id === (preset.repositoryId || s.repositoryId))?.id ?? repos[0]?.id ?? "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, list.length, preset.projectId, preset.repositoryId]);
  const project = list.find((p) => p.id === f.projectId);
  const repos = project?.repositories.filter((r) => r.authorized) ?? [];

  const create = useAction(
    (b: CreateMissionInput) => post<{ id: string; key: string; flow: string }>("/missions", b),
    { ok: (m) => `${m.key} creada · flujo ${m.flow?.toUpperCase().replace("_", " ")}`, invalidate: [["missions"], ["overview"], ["projects"]], onDone: (m) => { close(); setF((s) => ({ ...s, title: "", objective: "", changeClass: "other", budgetM: "" })); nav(`/missions/${m.id}`); } },
  );
  const valid = f.title.trim().length >= 3 && f.objective.trim().length >= 10 && f.projectId && f.repositoryId;
  return (
    <Dialog open={isOpen} onClose={close} title="Nueva misión">
      <form className="flex flex-col gap-3.5" onSubmit={(e) => { e.preventDefault(); if (!valid) return; create.mutate({ title: f.title.trim(), objective: f.objective.trim(), projectId: f.projectId, repositoryId: f.repositoryId, priority: f.priority as "medium", changeClass: f.changeClass === "other" ? undefined : (f.changeClass as never), budgetTokens: f.budgetM ? Math.round(Number(f.budgetM) * 1_000_000) : undefined, autoStart: true }); }}>
        <Field label="Título"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Ej. Añadir exportación CSV" autoFocus maxLength={160} /></Field>
        <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
          <Field label="Proyecto"><Select value={f.projectId} onChange={(e) => setF({ ...f, projectId: e.target.value, repositoryId: "" })}>{list.map((p) => <option key={p.id} value={p.id}>{p.companyName} · {p.name}</option>)}</Select></Field>
          <Field label="Repositorio"><Select value={f.repositoryId} onChange={(e) => setF({ ...f, repositoryId: e.target.value })}>{repos.length ? repos.map((r) => <option key={r.id} value={r.id}>{r.fullName.split("/")[1]}</option>) : <option value="">Sin repos autorizados</option>}</Select></Field>
          <Field label="Prioridad"><Select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}><option value="high">Alta</option><option value="medium">Media</option><option value="low">Baja</option></Select></Field>
          <Field label="Límite de tokens (M, opcional)"><Input value={f.budgetM} onChange={(e) => setF({ ...f, budgetM: e.target.value.replace(/[^\d.]/g, "") })} inputMode="decimal" placeholder="Por defecto de Ajustes" /></Field>
        </div>
        <Field label="Qué quieres conseguir"><Textarea rows={3} value={f.objective} onChange={(e) => setF({ ...f, objective: e.target.value })} placeholder="Describe el resultado esperado en 1-2 frases." maxLength={8000} /></Field>
        <Field label="Tipo de cambio">
          <Select value={f.changeClass} onChange={(e) => setF({ ...f, changeClass: e.target.value })}>{["other", ...CHANGE_CLASSES.filter((c) => c !== "other")].map((c) => <option key={c} value={c}>{CLASS_LABEL[c]}</option>)}</Select>
        </Field>
        <p className="m-0 text-[12px] leading-snug text-mu">Solo los cambios triviales pueden aspirar a FAST TRACK, y únicamente si el motor de políticas lo verifica con los ficheros reales. El flujo final lo decide el backend, no esta casilla.</p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" onClick={close}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={!valid} loading={create.isPending}>Crear misión</Button>
        </div>
      </form>
    </Dialog>
  );
}
